import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LocalDate } from "@/components/ui/local-date";
import { toast } from "@/components/ui/use-toast";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { cn, formatCurrency } from "@/lib/utils";
import { Link, createFileRoute, notFound, redirect, useRouter } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * `/accounting/contacts/$id` — one partner's page.
 *
 * It answers what you open a partner for: who they are on a document,
 * what is open between us in both directions, what we have exchanged,
 * and which Hub customers bill through them. The identity is editable
 * here because this is where somebody looks when an invoice came out
 * with the wrong address — and nowhere else offers it.
 *
 * Editing a contact never touches a document already issued: the
 * identity is copied onto the document when it is issued, so this page
 * changes what the *next* invoice will say.
 */

type Contact = {
  id: string;
  name: string;
  role: "customer" | "supplier" | "both";
  registrationNo: string | null;
  vatNo: string | null;
  email: string | null;
  phone: string | null;
  street: string | null;
  city: string | null;
  zip: string | null;
  country: string;
  defaultDueDays: number | null;
  currency: string;
  note: string | null;
  active: boolean;
  customers: Array<{ id: string; name: string }>;
  suppliers: Array<{ id: string; name: string }>;
};

type DocumentRow = {
  id: string;
  number: string | null;
  issuedOn: string;
  dueOn: string;
  total: string;
  paidAmount: string;
  currency: string;
  status: string;
};

type Totals = { unpaidCount: number; unpaid: string; overdueCount: number; overdue: string };

type Detail = {
  contact: Contact;
  issued: DocumentRow[];
  received: DocumentRow[];
  balances: { owedToUs: Totals; owedByUs: Totals };
};

const ROLE_LABEL: Record<Contact["role"], string> = {
  customer: "customer",
  supplier: "supplier",
  both: "customer + supplier",
};

const statusVariant: Record<string, "success" | "warning" | "secondary" | "outline"> = {
  paid: "success",
  partially_paid: "warning",
  issued: "outline",
  draft: "secondary",
  cancelled: "secondary",
};

const INPUT =
  "mt-1 block h-9 w-full rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export const Route = createFileRoute("/_app/accounting/contacts/$id")({
  loader: async ({ params }) => {
    try {
      return await api.get<Detail>(`/api/accounting/contacts/${params.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      if (err instanceof ApiError && err.status === 404) throw notFound();
      throw err;
    }
  },
  component: ContactPage,
});

function ContactPage() {
  const { contact, issued, received, balances } = Route.useLoaderData();
  const router = useRouter();
  const [editing, setEditing] = useState(false);

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <Link
            to="/accounting/contacts"
            className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Contacts
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="font-semibold text-2xl">{contact.name}</h1>
            <Badge variant={contact.role === "both" ? "success" : "outline"}>
              {ROLE_LABEL[contact.role]}
            </Badge>
            {!contact.active && <Badge variant="secondary">inactive</Badge>}
          </div>
          <p className="text-muted-foreground text-sm">
            {[
              contact.street,
              [contact.zip, contact.city].filter(Boolean).join(" "),
              contact.country,
            ]
              .filter(Boolean)
              .join(", ") || "No address on file"}
          </p>
        </div>
        <Button variant="outline" onClick={() => setEditing((value) => !value)}>
          {editing ? "Close" : "Edit"}
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Figure
          label={`They owe us (${balances.owedToUs.unpaidCount})`}
          value={formatCurrency(Number(balances.owedToUs.unpaid))}
        />
        <Figure
          label={`Of that overdue (${balances.owedToUs.overdueCount})`}
          value={formatCurrency(Number(balances.owedToUs.overdue))}
          tone={Number(balances.owedToUs.overdue) > 0 ? "danger" : undefined}
        />
        <Figure
          label={`We owe them (${balances.owedByUs.unpaidCount})`}
          value={formatCurrency(Number(balances.owedByUs.unpaid))}
        />
        <Figure
          label={`Of that overdue (${balances.owedByUs.overdueCount})`}
          value={formatCurrency(Number(balances.owedByUs.overdue))}
          tone={Number(balances.owedByUs.overdue) > 0 ? "danger" : undefined}
        />
      </div>

      {editing && (
        <ContactForm
          contact={contact}
          onDone={() => {
            setEditing(false);
            void router.invalidate();
          }}
        />
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Billing identity</CardTitle>
            <CardDescription>What lands on the next document we issue them.</CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <dt className="text-muted-foreground">Registration no.</dt>
              <dd className="tabular-nums">{contact.registrationNo ?? "—"}</dd>
              <dt className="text-muted-foreground">VAT no.</dt>
              <dd>{contact.vatNo ?? "—"}</dd>
              <dt className="text-muted-foreground">Email</dt>
              <dd className="truncate">{contact.email ?? "—"}</dd>
              <dt className="text-muted-foreground">Phone</dt>
              <dd>{contact.phone ?? "—"}</dd>
              <dt className="text-muted-foreground">Due in</dt>
              <dd>
                {contact.defaultDueDays === null
                  ? "Default from settings"
                  : `${contact.defaultDueDays} days`}
              </dd>
              <dt className="text-muted-foreground">Currency</dt>
              <dd>{contact.currency}</dd>
            </dl>
            {contact.note && (
              <p className="mt-3 border-t pt-3 text-muted-foreground text-sm">{contact.note}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Linked Hub customers</CardTitle>
            <CardDescription>Who bills through this contact.</CardDescription>
          </CardHeader>
          <CardContent>
            <CustomerLinks contact={contact} onDone={() => router.invalidate()} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">As a supplier</CardTitle>
            <CardDescription>Supplier records tied to this contact.</CardDescription>
          </CardHeader>
          <CardContent className="text-sm">
            {contact.suppliers.length === 0 ? (
              <p className="text-muted-foreground">
                None — nothing they invoiced us has been tied to a supplier record.
              </p>
            ) : (
              <ul className="space-y-1">
                {contact.suppliers.map((supplier) => (
                  <li key={supplier.id}>{supplier.name}</li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <DocumentCard title="We invoiced them" rows={issued} side="issued" />
        <DocumentCard title="They invoiced us" rows={received} side="received" />
      </div>
    </div>
  );
}

function DocumentCard({
  title,
  rows,
  side,
}: {
  title: string;
  rows: DocumentRow[];
  side: "issued" | "received";
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base">{title}</CardTitle>
          <CardDescription>The last twenty.</CardDescription>
        </div>
        <Link
          to={side === "issued" ? "/accounting/invoices" : "/accounting/expenses"}
          className="text-muted-foreground text-sm underline"
        >
          All
        </Link>
      </CardHeader>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <p className="p-6 text-muted-foreground text-sm">Nothing yet.</p>
        ) : (
          <ul className="divide-y">
            {rows.map((row) => (
              <li key={row.id}>
                <Link
                  to={side === "issued" ? "/accounting/invoices/$id" : "/accounting/expenses/$id"}
                  params={{ id: row.id }}
                  className="flex flex-wrap items-center gap-3 px-6 py-2 text-sm hover:bg-muted/50"
                >
                  <span className="w-28 font-mono text-xs">{row.number ?? "—"}</span>
                  <LocalDate value={row.issuedOn} className="text-muted-foreground" />
                  <Badge variant={statusVariant[row.status] ?? "outline"} className="text-[10px]">
                    {row.status.replace("_", " ")}
                  </Badge>
                  <span className="flex-1 text-right tabular-nums">
                    {formatCurrency(Number(row.total), row.currency)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function CustomerLinks({ contact, onDone }: { contact: Contact; onDone: () => void }) {
  const [customers, setCustomers] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    let live = true;
    api
      .get<{ customers: Array<{ id: string; name: string }> }>("/api/customers?pageSize=200")
      .then((result) => live && setCustomers(result.customers ?? []))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  const link = useApiMutation(
    (customerId: string) =>
      api.post(`/api/accounting/contacts/${contact.id}/customers`, { customerId }),
    { onSuccess: onDone, errorMessage: () => "Couldn't link the customer." },
  );
  const unlink = useApiMutation(
    (customerId: string) =>
      api.delete(`/api/accounting/contacts/${contact.id}/customers/${customerId}`),
    { onSuccess: onDone, errorMessage: () => "Couldn't unlink the customer." },
  );

  return (
    <div className="space-y-3 text-sm">
      {contact.customers.length === 0 ? (
        <p className="text-muted-foreground">
          None — this contact is billed directly, without a project side.
        </p>
      ) : (
        <ul className="space-y-1">
          {contact.customers.map((customer) => (
            <li key={customer.id} className="flex items-center justify-between gap-2">
              <Link to="/customers/$id" params={{ id: customer.id }} className="hover:underline">
                {customer.name}
              </Link>
              <button
                type="button"
                className="text-muted-foreground text-xs underline"
                onClick={() => unlink.mutate(customer.id)}
              >
                unlink
              </button>
            </li>
          ))}
        </ul>
      )}

      <select
        className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
        value=""
        onChange={(e) => e.target.value && link.mutate(e.target.value)}
      >
        <option value="">Link a customer…</option>
        {customers
          .filter((customer) => !contact.customers.some((c) => c.id === customer.id))
          .map((customer) => (
            <option key={customer.id} value={customer.id}>
              {customer.name}
            </option>
          ))}
      </select>
      {(link.error || unlink.error) && (
        <p className="text-destructive text-xs">{link.error ?? unlink.error}</p>
      )}
    </div>
  );
}

function ContactForm({ contact, onDone }: { contact: Contact; onDone: () => void }) {
  const save = useApiMutation(
    (body: Record<string, unknown>) => api.patch(`/api/accounting/contacts/${contact.id}`, body),
    {
      onSuccess: () => {
        toast({ title: "Contact saved" });
        onDone();
      },
      errorMessage: () => "Couldn't save the contact.",
    },
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Edit contact</CardTitle>
        <CardDescription>
          Documents already issued keep the identity they were issued with — this changes what the
          next one says.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="grid gap-3 md:grid-cols-3"
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            const value = (key: string) => {
              const raw = String(form.get(key) ?? "").trim();
              return raw.length > 0 ? raw : null;
            };
            save.mutate({
              name: String(form.get("name") ?? "").trim(),
              role: String(form.get("role") ?? contact.role),
              registrationNo: value("registrationNo"),
              vatNo: value("vatNo"),
              email: value("email"),
              phone: value("phone"),
              street: value("street"),
              city: value("city"),
              zip: value("zip"),
              country: String(form.get("country") ?? "CZ"),
              defaultDueDays: dueDays(form),
              currency: String(form.get("currency") ?? contact.currency),
              note: value("note"),
              active: form.get("active") === "on",
            });
          }}
        >
          <Field name="name" label="Name" defaultValue={contact.name} required />
          <label className="text-sm">
            <span className="text-muted-foreground text-xs">Role</span>
            <select name="role" defaultValue={contact.role} className={INPUT}>
              <option value="customer">customer</option>
              <option value="supplier">supplier</option>
              <option value="both">customer + supplier</option>
            </select>
          </label>
          <Field
            name="registrationNo"
            label="Registration no."
            defaultValue={contact.registrationNo ?? ""}
          />
          <Field name="vatNo" label="VAT no." defaultValue={contact.vatNo ?? ""} />
          <Field name="email" label="Email" type="email" defaultValue={contact.email ?? ""} />
          <Field name="phone" label="Phone" defaultValue={contact.phone ?? ""} />
          <Field name="street" label="Street" defaultValue={contact.street ?? ""} />
          <Field name="zip" label="ZIP" defaultValue={contact.zip ?? ""} />
          <Field name="city" label="City" defaultValue={contact.city ?? ""} />
          <Field name="country" label="Country" defaultValue={contact.country} />
          <Field
            name="defaultDueDays"
            label="Due in (days)"
            type="number"
            defaultValue={contact.defaultDueDays === null ? "" : String(contact.defaultDueDays)}
            placeholder="Default from settings"
          />
          <Field name="currency" label="Currency" defaultValue={contact.currency} />
          <Field name="note" label="Note" defaultValue={contact.note ?? ""} />
          <label className="flex items-end gap-2 text-sm">
            <input
              type="checkbox"
              name="active"
              defaultChecked={contact.active}
              className="mb-2 h-4 w-4"
            />
            <span className="mb-1.5">Active</span>
          </label>
          <div className="flex items-end">
            <Button type="submit" disabled={save.pending}>
              {save.pending ? "Saving…" : "Save"}
            </Button>
          </div>
          {save.error && <p className="text-destructive text-sm md:col-span-3">{save.error}</p>}
        </form>
      </CardContent>
    </Card>
  );
}

const dueDays = (form: FormData): number | null => {
  const raw = String(form.get("defaultDueDays") ?? "").trim();
  return raw.length > 0 ? Number(raw) : null;
};

function Field(props: {
  name: string;
  label: string;
  type?: string;
  defaultValue?: string;
  placeholder?: string;
  required?: boolean;
}) {
  const { label, ...rest } = props;
  return (
    <label className="text-sm">
      <span className="text-muted-foreground text-xs">{label}</span>
      <input {...rest} className={INPUT} />
    </label>
  );
}

function Figure({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "danger" | undefined;
}) {
  return (
    <div className="rounded-md border bg-card p-4">
      <div className="text-muted-foreground text-xs">{label}</div>
      <div
        className={cn("font-semibold text-xl tabular-nums", tone === "danger" && "text-red-600")}
      >
        {value}
      </div>
    </div>
  );
}
